import { Link } from "react-router-dom";
import { ArrowUpRight, Images, Code } from "lucide-react";
import { Project } from "@/data/projects";
import { Badge } from "@/components/ui/badge";
import { BlurImage } from "@/components/ui/blur-image";
import { HtmlContent } from "@/components/ui/html-content";
import { upgradeYouTubeThumbnailQuality } from "@/lib/youtube";

interface ProjectCardProps {
  project: Project;
  index: number;
  priority?: boolean;
}

export function ProjectCard({ project, index, priority = false }: ProjectCardProps) {
  const thumbnailUrl = upgradeYouTubeThumbnailQuality(project.thumbnail);
  const imageCount = (project.images?.length || 0) + 1; // +1 for thumbnail
  const hasCodeSnippet = project.hasCodeSnippet ?? !!project.codeSnippet?.code;

  return (
    <article>
      <Link
        to={`/portfolio/${project.slug}`}
        className="group block overflow-hidden rounded-xl bg-card border border-border hover:border-primary/50 transition-all duration-300"
      >
        {/* Always show thumbnail image */}
        <div className="relative overflow-hidden bg-muted">
          <BlurImage
            src={thumbnailUrl}
            alt={project.title}
            width={project.thumbnailWidth}
            height={project.thumbnailHeight}
            className="w-full transition-transform duration-500 group-hover:scale-105"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            priority={priority}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-card/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Badges */}
          <div className="absolute top-2 right-2 flex items-center gap-2">
            {/* Code snippet indicator */}
            {hasCodeSnippet && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-primary/90 text-primary-foreground text-xs font-medium">
                <Code size={12} />
                <span>Code</span>
              </div>
            )}
            {/* Multiple images indicator */}
            {imageCount > 1 && !hasCodeSnippet && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 text-white text-xs font-medium">
                <Images size={12} />
                <span>{imageCount}</span>
              </div>
            )}
          </div>
          
          {/* Hover overlay */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium">
              View Project <ArrowUpRight size={16} />
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          <div className="flex gap-2 mb-3 flex-wrap">
            {project.category.split(",").map((cat) => (
              <Badge key={cat.trim()} variant="secondary">
                {cat.trim()}
              </Badge>
            ))}
          </div>
          <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-1">
            {project.title}
          </h3>
          <HtmlContent 
            content={project.description}
            className="mt-2 text-sm text-muted-foreground line-clamp-2 [&_p]:m-0"
          />
          <div className="mt-4 flex flex-wrap gap-2">
            {project.technologies.slice(0, 3).map((tech) => (
              <span
                key={tech}
                className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground"
              >
                {tech}
              </span>
            ))}
            {project.technologies.length > 3 && (
              <span className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground">
                +{project.technologies.length - 3}
              </span>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
}
